import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import Ellipsis from "@/components/views/Ellipsis";
import { formatDateRange, formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions, Flex, Form, Modal, Popover } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration, useGetAgentEnumerations } from "../../../../common/api/queries";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { formatAgentDirectorateName, formatAgentName } from "../../../agent/utils";
import { EnumerationType } from "../../../enumerations/enums";
import { ContractFilterPageRequest } from "../../types";
import { ContractExtendedFilterFormFields } from "./ContractExtendedFilterFormFields";

type ModalFieldsForm = Pick<
  ContractFilterPageRequest,
  | "signerIds"
  | "managerIds"
  | "createdAtMin"
  | "createdAtMax"
  | "mediationReportSignDateMin"
  | "mediationReportSignDateMax"
  | "effectiveBeginningDateMin"
  | "effectiveBeginningDateMax"
  | "signDateMin"
  | "signDateMax"
  | "origins"
  | "commissionBatchIds"
  | "effectiveEndDateOrLoanMaturityDateMin"
  | "effectiveEndDateOrLoanMaturityDateMax"
  | "cancellationDateMin"
  | "cancellationDateMax"
  | "affiliatePartnerIds"
  | "transferredFromOtherBroker"
  | "transferredToOtherBrokerDateMin"
  | "transferredToOtherBrokerDateMax"
  | "vehicleFirstRegistrationYearMin"
  | "vehicleFirstRegistrationYearMax"
  | "annualPremiumOrApprovedAmountMin"
  | "annualPremiumOrApprovedAmountMax"
  | "investmentFeeTypes"
  | "commoditySavingsPlans"
  | "onlyIncludedInVerificationExport"
  | "signerDirectorateIds"
  | "signerDirectSubordinateId"
  | "signerAllSubordinateId"
  | "gainerDirectSubordinateId"
  | "gainerAllSubordinateId"
  | "gainerDirectorateIds"
  | "managerAllSubordinateId"
  | "managerDirectSubordinateId"
  | "managerDirectorateIds"
>;

type Props = {
  onSubmit: (values: ContractFilterPageRequest) => void;
};

export const ContractExtendedFilterModal = ({ onSubmit }: Props) => {
  const { t } = useTranslation();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const affiliatePartnersEnums = useEnumeration(EnumerationType.AFFILIATE_PARTNERS);
  const commissionsBatchesEnums = useEnumeration(EnumerationType.COMMISSIONS_BATCHES);
  const directoratesEnums = useEnumeration(EnumerationType.AGENT_DIRECTORATES);

  const parentForm = Form.useFormInstance<ContractFilterPageRequest>();
  const parentFormValues = Form.useWatch([], { form: parentForm, preserve: true }) as
    ContractFilterPageRequest | undefined;

  const signerDirectSubordinateId = parentFormValues?.signerDirectSubordinateId as UUID | undefined;
  const signerAllSubordinateId = parentFormValues?.signerAllSubordinateId as UUID | undefined;

  const managerDirectSubordinateId = parentFormValues?.managerDirectSubordinateId as UUID | undefined;
  const managerAllSubordinateId = parentFormValues?.managerAllSubordinateId as UUID | undefined;

  const subordinateIds = [
    signerDirectSubordinateId,
    signerAllSubordinateId,
    managerDirectSubordinateId,
    managerAllSubordinateId,
  ].filter((id) => id !== undefined);

  const [form] = Form.useForm<ModalFieldsForm>();

  const { data: signers } = useGetAgentEnumerations(parentFormValues?.signerIds ?? []);
  const { data: managers } = useGetAgentEnumerations(parentFormValues?.managerIds ?? []);
  const { data: subordinateAgents } = useGetAgentEnumerations(subordinateIds);

  useEffect(() => {
    if (isFilterModalOpen && parentFormValues) {
      form.setFieldsValue(parentFormValues);
    }
  }, [isFilterModalOpen, form, parentFormValues]);

  const formattedFilterResults: (DescriptionsItemType & { additionalFields?: (keyof ContractFilterPageRequest)[] })[] =
    useMemo(() => {
      const result: (DescriptionsItemType & { additionalFields?: (keyof ContractFilterPageRequest)[] })[] = [];

      if (!parentFormValues) {
        return result;
      }

      if (signers?.length) {
        const data: string[] = [];

        signers.forEach((agent) => data.push(formatAgentName(agent) ?? ""));

        result.push({
          key: "signerIds",
          label: t("contract.filter.signerIds"),
          children: data.join(", "),
        });
      }

      if (parentFormValues.signerDirectorateIds?.length) {
        const data: string[] = [];

        parentFormValues.signerDirectorateIds.forEach((directorateId) => {
          const directorate = directoratesEnums.find((item) => item.id === directorateId);

          data.push(directorate ? formatAgentDirectorateName(directorate) : "");
        });

        result.push({
          key: "signerDirectorateIds",
          label: t("contract.filter.signerDirectorateIds"),
          children: data.join(", "),
        });
      }

      const signerDirectSubordinate = subordinateAgents?.find((agent) => agent.id === signerDirectSubordinateId);

      if (signerDirectSubordinate) {
        result.push({
          key: "signerDirectSubordinateId",
          label: t("contract.filter.signerDirectSubordinateId"),
          children: formatAgentName(signerDirectSubordinate),
        });
      }

      const signerAllSubordinate = subordinateAgents?.find((agent) => agent.id === signerAllSubordinateId);

      if (signerAllSubordinate) {
        result.push({
          key: "signerAllSubordinateId",
          label: t("contract.filter.signerAllSubordinateId"),
          children: formatAgentName(signerAllSubordinate),
        });
      }

      if (managers?.length) {
        const data: string[] = [];

        managers.forEach((agent) => data.push(formatAgentName(agent) ?? ""));

        result.push({
          key: "managerIds",
          label: t("contract.filter.managerIds"),
          children: data.join(", "),
        });
      }

      if (parentFormValues.managerDirectorateIds?.length) {
        const data: string[] = [];

        parentFormValues.managerDirectorateIds.forEach((directorateId) => {
          const directorate = directoratesEnums.find((item) => item.id === directorateId);

          data.push(directorate ? formatAgentDirectorateName(directorate) : "");
        });

        result.push({
          key: "managerDirectorateIds",
          label: t("contract.filter.managerDirectorateIds"),
          children: data.join(", "),
        });
      }

      const managerDirectSubordinate = subordinateAgents?.find((agent) => agent.id === managerDirectSubordinateId);

      if (managerDirectSubordinate) {
        result.push({
          key: "managerDirectSubordinateId",
          label: t("contract.filter.managerDirectSubordinateId"),
          children: formatAgentName(managerDirectSubordinate),
        });
      }

      const managerAllSubordinate = subordinateAgents?.find((agent) => agent.id === managerAllSubordinateId);

      if (managerAllSubordinate) {
        result.push({
          key: "managerAllSubordinateId",
          label: t("contract.filter.managerAllSubordinateId"),
          children: formatAgentName(managerAllSubordinate),
        });
      }

      if (parentFormValues.createdAtMin && parentFormValues.createdAtMax) {
        result.push({
          key: "createdAtRange",
          label: t("contract.filter.createdAtRange"),
          additionalFields: ["createdAtMin", "createdAtMax"],
          children: formatDateRange(parentFormValues.createdAtMin, parentFormValues.createdAtMax),
        });
      }

      if (parentFormValues.createdByKeyword) {
        result.push({
          key: "createdByKeyword",
          label: t("contract.filter.createdByKeyword"),
          children: parentFormValues.createdByKeyword,
        });
      }

      if (parentFormValues.verifiedAtMin && parentFormValues.verifiedAtMax) {
        result.push({
          key: "verifiedAtRange",
          label: t("contract.filter.verifiedAtRange"),
          additionalFields: ["verifiedAtMin", "verifiedAtMax"],
          children: formatDateRange(parentFormValues.verifiedAtMin, parentFormValues.verifiedAtMax),
        });
      }

      if (parentFormValues.verifiedByKeyword) {
        result.push({
          key: "verifiedByKeyword",
          label: t("contract.filter.verifiedByKeyword"),
          children: parentFormValues.verifiedByKeyword,
        });
      }

      if (parentFormValues.mediationReportSignDateMin && parentFormValues.mediationReportSignDateMax) {
        result.push({
          key: "mediationReportSignDateRange",
          label: t("contract.filter.mediationReportSignDateRange"),
          additionalFields: ["mediationReportSignDateMin", "mediationReportSignDateMax"],
          children: formatDateRange(
            parentFormValues.mediationReportSignDateMin,
            parentFormValues.mediationReportSignDateMax,
          ),
        });
      }

      if (parentFormValues.effectiveBeginningDateMin && parentFormValues.effectiveBeginningDateMax) {
        result.push({
          key: "effectiveBeginningDateRange",
          label: t("contract.filter.effectiveBeginningDateRange"),
          additionalFields: ["effectiveBeginningDateMin", "effectiveBeginningDateMax"],
          children: formatDateRange(
            parentFormValues.effectiveBeginningDateMin,
            parentFormValues.effectiveBeginningDateMax,
          ),
        });
      }

      if (parentFormValues.signDateMin && parentFormValues.signDateMax) {
        result.push({
          key: "signDateRange",
          label: t("contract.filter.signDateRange"),
          additionalFields: ["signDateMin", "signDateMax"],
          children: formatDateRange(parentFormValues.signDateMin, parentFormValues.signDateMax),
        });
      }

      if (parentFormValues.effectiveBeginningDateMonths) {
        result.push({
          key: "effectiveBeginningDateMonths",
          label: t("contract.filter.effectiveBeginningDateMonths"),
          children: parentFormValues.effectiveBeginningDateMonths
            .map((month) => t(`common.months.${month}`))
            .join(", "),
        });
      }

      if (parentFormValues.signDateMonths) {
        result.push({
          key: "signDateMonths",
          label: t("contract.filter.signDateMonths"),
          children: parentFormValues.signDateMonths.map((month) => t(`common.months.${month}`)).join(", "),
        });
      }

      if (parentFormValues.origins?.length) {
        result.push({
          key: "origins",
          label: t("contract.enums.contractOrigin._label"),
          children: parentFormValues.origins.map((origin) => t(`contract.enums.contractOrigin.${origin}`)).join(", "),
        });
      }

      if (
        parentFormValues.effectiveEndDateOrLoanMaturityDateMin &&
        parentFormValues.effectiveEndDateOrLoanMaturityDateMax
      ) {
        result.push({
          key: "effectiveEndDateOrLoanMaturityDateRange",
          label: t("contract.filter.effectiveEndDateOrLoanMaturityDateRange"),
          additionalFields: ["effectiveEndDateOrLoanMaturityDateMin", "effectiveEndDateOrLoanMaturityDateMax"],
          children: formatDateRange(
            parentFormValues.effectiveEndDateOrLoanMaturityDateMin,
            parentFormValues.effectiveEndDateOrLoanMaturityDateMax,
          ),
        });
      }

      if (parentFormValues.cancellationDateMin && parentFormValues.cancellationDateMax) {
        result.push({
          key: "cancellationDateRange",
          label: t("contract.filter.cancellationDateRange"),
          additionalFields: ["cancellationDateMin", "cancellationDateMax"],
          children: formatDateRange(parentFormValues.cancellationDateMin, parentFormValues.cancellationDateMax),
        });
      }

      if (parentFormValues.affiliatePartnerIds?.length) {
        const data: string[] = [];

        parentFormValues.affiliatePartnerIds.forEach((id) => {
          data.push(affiliatePartnersEnums.find((item) => item.id === id)?.name ?? "");
        });

        result.push({
          key: "affiliatePartnerIds",
          label: t("contract.filter.affiliatePartners"),
          children: data.join(", "),
        });
      }

      if (parentFormValues.transferredFromOtherBroker) {
        result.push({
          key: "transferredFromOtherBroker",
          label: t("contract.filter.transferredFromOtherBroker"),
          children: t("common.yes"),
        });
      }

      if (parentFormValues.transferredToOtherBrokerDateMin && parentFormValues.transferredToOtherBrokerDateMax) {
        result.push({
          key: "transferredToOtherBrokerDateRange",
          label: t("contract.filter.transferredToOtherBrokerDateRange"),
          additionalFields: ["transferredToOtherBrokerDateMin", "transferredToOtherBrokerDateMax"],
          children: formatDateRange(
            parentFormValues.transferredToOtherBrokerDateMin,
            parentFormValues.transferredToOtherBrokerDateMax,
          ),
        });
      }

      if (parentFormValues.vehicleFirstRegistrationYearMin && parentFormValues.vehicleFirstRegistrationYearMax) {
        result.push({
          key: "vehicleFirstRegistrationYearRange",
          label: t("contract.filter.vehicleFirstRegistrationYearRange"),
          additionalFields: ["vehicleFirstRegistrationYearMin", "vehicleFirstRegistrationYearMax"],
          children: `${parentFormValues.vehicleFirstRegistrationYearMin} ~ ${parentFormValues.vehicleFirstRegistrationYearMax}`,
        });
      }

      if (
        isDefined(parentFormValues.annualPremiumOrApprovedAmountMin) &&
        isDefined(parentFormValues.annualPremiumOrApprovedAmountMax)
      ) {
        result.push({
          key: "annualPremiumOrApprovedAmountRange",
          label: t("contract.filter.annualPremiumOrApprovedAmountRange"),
          additionalFields: ["annualPremiumOrApprovedAmountMin", "annualPremiumOrApprovedAmountMax"],
          children: `${formatLocaleCurrency(parentFormValues.annualPremiumOrApprovedAmountMin)} ~ ${formatLocaleCurrency(parentFormValues.annualPremiumOrApprovedAmountMax)}`,
        });
      } else if (isDefined(parentFormValues.annualPremiumOrApprovedAmountMin)) {
        result.push({
          key: "annualPremiumOrApprovedAmountMin",
          label: t("contract.filter.attrs.annualPremiumOrApprovedAmountMin"),
          children: formatLocaleCurrency(parentFormValues.annualPremiumOrApprovedAmountMin),
        });
      } else if (isDefined(parentFormValues.annualPremiumOrApprovedAmountMax)) {
        result.push({
          key: "annualPremiumOrApprovedAmountMax",
          label: t("contract.filter.attrs.annualPremiumOrApprovedAmountMax"),
          children: formatLocaleCurrency(parentFormValues.annualPremiumOrApprovedAmountMax),
        });
      }

      if (parentFormValues.onlyIncludedInVerificationExport) {
        result.push({
          key: "onlyIncludedInVerificationExport",
          label: t("contract.filter.onlyIncludedInVerificationExport"),
          children: t("common.yes"),
        });
      }

      if (parentFormValues.investmentFeeTypes?.length) {
        const data: string[] = [];

        parentFormValues.investmentFeeTypes.forEach((feeType) => {
          data.push(t(`contract.enums.investmentFeeType.${feeType}`));
        });

        result.push({
          key: "investmentFeeTypes",
          label: t("contract.filter.investmentFeeTypes"),
          children: data.join(", "),
        });
      }

      if (parentFormValues.commoditySavingsPlans?.length) {
        const data: string[] = [];

        parentFormValues.commoditySavingsPlans.forEach((savingsPlan) => {
          data.push(t(`contract.enums.commoditySavingsPlan.${savingsPlan}`));
        });

        result.push({
          key: "commoditySavingsPlans",
          label: t("contract.filter.commoditySavingsPlans"),
          children: data.join(", "),
        });
      }

      if (parentFormValues.commissionBatchIds?.length) {
        const data: string[] = [];

        parentFormValues.commissionBatchIds.forEach((id) => {
          data.push(commissionsBatchesEnums.find((item) => item.id === id)?.name ?? "");
        });

        result.push({
          key: "commissionBatchIds",
          label: t("contract.filter.commissionBatchIds"),
          children: (
            <div style={{ maxWidth: 200 }}>
              <Ellipsis>{data.join(", ")}</Ellipsis>
            </div>
          ),
        });
      }

      return result;
    }, [signers, managers, parentFormValues, subordinateAgents]);

  return (
    <>
      <ContractExtendedFilterFormFields hidden />

      <Flex align="center">
        <SecondaryButton icon={<AntIcon type="search" />} onClick={() => setIsFilterModalOpen(true)}>
          {t("common.extendedFilter")}
        </SecondaryButton>

        <div className="margin-left-small">
          {formattedFilterResults.length ? (
            <>
              <Popover
                placement="bottom"
                title={t("contract.filter.activeFilter")}
                content={<Descriptions bordered size="small" column={1} items={formattedFilterResults} />}
              >
                <span className="sub-header-info margin-right-tiny cursor-pointer">
                  {t("contract.filter.helpers.extendedFilterActiveHint")} <AntIcon type="question" />
                </span>
              </Popover>

              <ActionButton
                variant="text"
                color="danger"
                icon="delete"
                onClick={() => {
                  onSubmit(
                    formattedFilterResults.reduce<ContractFilterPageRequest>((acc, value) => {
                      if (parentFormValues?.[value.key as keyof ModalFieldsForm] === true) {
                        (acc as any)[value.key as string] = false;
                      } else {
                        (acc as any)[value.key as string] = undefined;
                      }

                      value.additionalFields?.forEach((field) => {
                        if (parentFormValues?.[field] === true) {
                          (acc as any)[field] = false;
                        } else {
                          (acc as any)[field] = undefined;
                        }
                      });

                      return acc;
                    }, {}),
                  );
                }}
              >
                {t("common.cancel")}
              </ActionButton>
            </>
          ) : undefined}
        </div>

        <Modal
          width={ModalSizes.MEDIUM}
          open={isFilterModalOpen}
          title={t("contract.filter.extended")}
          okText={t("contract.filter.submit")}
          okButtonProps={{ icon: <AntIcon type="search" /> }}
          cancelText={t("common.cancel")}
          forceRender
          onOk={() => {
            form.validateFields().then((values) => {
              onSubmit(values);
              setIsFilterModalOpen(false);
            });
          }}
          onCancel={() => setIsFilterModalOpen(false)}
          afterClose={() => form.resetFields()}
          mask={{ closable: false }}
        >
          <Form form={form} layout="vertical" name="contractExtendedFilterForm">
            <div style={{ display: "grid", gap: rowGutter, alignItems: "end", gridTemplateColumns: "1fr 1fr" }}>
              <ContractExtendedFilterFormFields />
            </div>
          </Form>
        </Modal>
      </Flex>
    </>
  );
};
